import { useState, useCallback, useEffect } from 'react';
import { type Process, type Lead, type Activities, type Comments, type CommentFormData, type ActivityFormData } from '../types/lead';
import { leadService } from '../services/domain/leadService';
import type { ContactForm, User, chatContactForm } from '../types/lead';
import type { DealFormData } from '../types/deal';
import { dealService } from '../services/domain/dealService';


interface UseLeadsResult {
  data: Lead | null;
  processes: Process[];
  programas: { id: number; name: string }[];
  activities: Activities[];
  users: User[];
  comments: Comments[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  createComment: (formData: CommentFormData) => Promise<{ success: boolean, message?: string }>;
  createActivity: (formData: ActivityFormData) => Promise<{ success: boolean, message?: string }>;
  updateStatus: (id: number, lead_process_id: number, lead_status_id: number) => Promise<{ success: boolean, message?: string }>;
  updateLead: (formData: Lead) => Promise<{ success: boolean, message?: string }>;
  createContact: (formData: ContactForm) => Promise<{ success: boolean, message?: string }>;
  createAssign: (lead_id: number, user_id: number) => Promise<{ success: boolean, message?: string }>;
  updateAssign: (lead_id: number, user_id: number) => Promise<{ success: boolean, message?: string }>;
  createDeal: (formData: DealFormData) => Promise<{ success: boolean, message?: string }>;
  updating: boolean;
  searching: boolean;
  searchContact: (data: chatContactForm) => Promise<{ success: boolean, error?: string, contactId?: string, conversationId?: string }>;
}

interface LeadParams {
  id: number | null;
}

export const useLeadDetail = (params: LeadParams): UseLeadsResult => {
  const [data, setData] = useState<Lead | null>(null);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [activities, setActivities] = useState<Activities[]>([]);
  const [programas, setProgramas] = useState<{ id: number; name: string }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [comments, setComments] = useState<Comments[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating] = useState(false);
  const [searching] = useState(false);

  const fetchProgramas = useCallback(async () => {

    try {
      const response = await leadService.getProgramas();
      setProgramas(response.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }

}, []);

  const fetchProcesses = useCallback(async () => {
    try {
      setError(null);
      const response = await leadService.getProcesses();

      // Ordenar por process_order antes de guardar en el estado
      const sortedProcesses = response.data.sort((a: any, b: any) => a.process_order - b.process_order);
      setProcesses(sortedProcesses);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setError(null);
      const response = await leadService.getUsers();

      setUsers(response.data.users);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    try {
      if (!params.id || params.id <= 0) {
        console.error('No hay ID válido para cargar');
        return;
      }
      setError(null);
      const response = await leadService.getActivities(params.id);

      setActivities(response.data)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  const fetchComments = useCallback(async () => {
    try {
      if (!params.id || params.id <= 0) {
        console.error('No hay ID válido para cargar');
        return;
      }
      setError(null);
      const response = await leadService.getComments(params.id);

      setComments(response.data)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  const fetchLead = useCallback(async () => {
    // Verificar que existe un ID válido
    if (!params.id || params.id <= 0) {
      console.error('No hay ID válido para cargar');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await leadService.getLeadsbyId(params.id);
      if (response) {
        setData(response);
      }

      await fetchProcesses();
      await fetchActivities();
      await fetchComments();
      await fetchUsers();
      await fetchProgramas();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching lead:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id, fetchProcesses, fetchProgramas]);

  useEffect(() => {
    if (params.id && params.id > 0) {
      fetchLead();
    }
  }, [params.id, fetchLead]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null);
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    } finally {
      setLoading(false)
    }
  }, [fetchLead]);

  const createComment = useCallback(async (
    formData: CommentFormData
  ): Promise<{ success: boolean }> => {
    try {
      setError(null);

      const personResult = await leadService.createComment(formData);
      if (personResult.error) {
        setError(`Error al crear persona: ${personResult.error}`);
      }

      await fetchComments();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false
      };
    } finally {

    }
  }, [fetchLead]);

  const createActivity = useCallback(async (
    formData: ActivityFormData
  ) => {
    try {
      setError(null);

      const result = await leadService.createActivity(formData);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchActivities();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchLead]);

  const createAssign = useCallback(async (
    lead_id: number, user_id: number
  ) => {
    try {
      setError(null);
      const result = await leadService.createAsesor(lead_id, user_id);
      if (result.error) {
        setError(`Error al crear lead: ${result.error}`);
        return {
          success: false,
        };
      }

      await fetchLead();

      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
    }
  }, [fetchLead]);

  const updateAssign = useCallback(async (
    lead_id: number, user_id: number
  ) => {
    try {
      setError(null);
      const result = await leadService.updateAsesor(lead_id, user_id);
      if (result.error) {
        setError(`Error al actualizar asignado ${result.error}`);
        return {
          success: false,
        };
      }

      await fetchLead();

      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
    }
  }, [fetchLead]);

  const updateStatus = useCallback(async (
    id: number, lead_process_id: number, lead_status_id: number
  ) => {
    try {
      setError(null);

      const result = await leadService.updateStatus(id, lead_process_id, lead_status_id);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchLead();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchLead]);

  const updateLead = useCallback(async (
    formData: Lead
  ) => {
    try {
      setError(null);

      const result = await leadService.updateLead(formData);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchLead();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchLead]);

  const createContact = useCallback(async (
    formData: ContactForm
  ) => {
    try {
      setError(null);

      const result = await leadService.createContact(formData);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchLead]);

  const createDeal = useCallback(async (
    formData: DealFormData
  ) => {
    try {
      setError(null);


      const result = await dealService.createDeal(formData);
      if (result.error) {
        setError(`Error al crear deal: ${result.error}`);
        return {
          success: false,
        };
      }
      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [fetchLead]);

  const searchContact = async (data: chatContactForm) => {
    try {
      if (!data) {
        return {
          success: false
        };
      }

      const searchContact = await leadService.searchPhoneLead(data.phone_number, data.lead_id);
      if (searchContact.data.isContact) {
        return {
          success: true,
          contactId: searchContact.data.idContact
        };
      }

      const createContact = await leadService.createContactChat(data)
      if (createContact.success && createContact.contactId) {
        return {
          success: true,
          contactId: createContact.contactId
        };
      }

      return {
        success: false,
        message: createContact.message
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  };


  return {
    data,
    processes,
    programas,
    activities,
    users,
    comments,
    loading,
    error,
    refetch,
    createComment,
    createActivity,
    updateStatus,
    updateLead,
    createContact,
    createAssign,
    updateAssign,
    createDeal,
    updating,
    searching,
    searchContact
  };
};